/**
 * The tour's state machine (TUTORIAL-STATE-001).
 *
 * Owns three things and nothing else: which step we are on, what the server has
 * been told, and what happens when a step turns out to have nothing to point
 * at. The drawing is in `TutorialSpotlight`, the invitation is in
 * `TutorialInvite`, and the list of stops is in `tutorialSteps.ts`.
 *
 * ── SKIPPING, AND WHY IT IS NOT A FILTER ───────────────────────────────────
 *
 * The tidy-looking version of this computes the visible steps up front —
 * filter the list by "is the anchor present", then walk what is left. It does
 * not work, for a reason worth writing down: the anchors are on different
 * PAGES. A step whose route is `/admin` cannot be resolved while we are on
 * `/guidance`, because its nav item is rendered but its page content is not,
 * and several anchors only exist after their route has loaded. Filtering up
 * front would decide a step was absent without ever having been where it
 * lives.
 *
 * So the walk is lazy: arrive at a step, navigate if it names a route, give the
 * anchor a bounded moment to appear, and if it does not, move on in whichever
 * direction we were already going. That last part matters — a step skipped
 * while going BACKWARDS has to skip backwards, or Back on step 6 lands you on
 * step 7 and the tour will not let you leave.
 *
 * ── WHAT IS NOT PERSISTED ──────────────────────────────────────────────────
 *
 * The index is local state, not server state. `lastStepKey` is written on each
 * advance so a resume knows where to start, but the tour does not read it back
 * mid-flight: a step's position in the list is a fact about the build, and a
 * key stored by an older build may no longer exist. `startIndexFor` resolves a
 * stored key to an index once, at the start, and falls back to the beginning if
 * it cannot — which is why an unrecognised `lastStepKey` is harmless.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  TUTORIAL_STATE_KEY,
  recordTutorialEvent,
  type TutorialEvent,
} from '../lib/tutorialApi';
import { ORIENTATION_STEPS, type TutorialStep } from './tutorialSteps';
import {
  anchorsFor,
  findAnchorElement,
  useTutorialAnchor,
  type AnchorOutcome,
} from './useTutorialAnchor';

export interface TutorialController {
  /** Null when the tour is not running. */
  step: TutorialStep | null;
  /**
   * 1-based, for "step 3 of 9" — over the steps THIS ACCOUNT will be shown,
   * not over the step list. See `showableSteps`.
   */
  position: number;
  total: number;
  anchor: AnchorOutcome;
  isFirst: boolean;
  isLast: boolean;
  running: boolean;
  start: (fromStepKey?: string | null) => void;
  next: () => void;
  back: () => void;
  /** Leave early. Records `declined`, keeping `lastStepKey` for a resume. */
  skip: () => void;
}

/**
 * Where a resume should start.
 *
 * An unknown key — a step renamed or removed since they last ran it — starts at
 * the beginning rather than throwing or guessing. Exported so the test can
 * assert that directly; it is the only part of the resume that can be wrong.
 */
export function startIndexFor(
  stepKey: string | null | undefined,
  steps: readonly TutorialStep[] = ORIENTATION_STEPS,
): number {
  if (!stepKey) return 0;
  const i = steps.findIndex((s) => s.key === stepKey);
  return i < 0 ? 0 : i;
}

/**
 * The steps this account will actually be shown.
 *
 * WHY IT EXISTS. A step whose anchor is not on screen is skipped — that is
 * deliberate, and it is how one tour serves every role. But the counter was
 * written over the step LIST, so a manager, who has no Admin hub, read
 * "Step 5 of 10", then "Step 7 of 10". The number jumped, and the progress bar
 * carried a segment that could never fill. Nothing was broken; the tour simply
 * told most of its users it was longer than it was, and appeared to lose a step
 * on the way past. Seen on 2026-09-25, walking the tour as a manager — a
 * director never sees it, because a director has every anchor.
 *
 * DECIDED ONCE, AT THE START, and that is the whole subtlety. Every anchor this
 * tour uses lives in the sidebar or the header, both mounted long before the
 * tour can be started, so asking now gives the same answer it will give in a
 * minute. A step that names no anchor is always shown. A step that names a
 * ROUTE is counted as shown even when its anchor is missing, because its screen
 * has not been navigated to yet — nothing declares a route today, and this is
 * here so that adding one does not quietly shorten the count.
 *
 * It is a count, not the navigation. The per-step resolver still decides what
 * is actually displayed — including an anchor that disappears mid-tour, which
 * this cannot know about.
 */
export function showableSteps(
  steps: readonly TutorialStep[] = ORIENTATION_STEPS,
): readonly TutorialStep[] {
  return steps.filter(
    (s) =>
      anchorsFor(s).length === 0 || s.route !== undefined || findAnchorElement(s) !== null,
  );
}

export function useTutorial(
  steps: readonly TutorialStep[] = ORIENTATION_STEPS,
): TutorialController {
  const [index, setIndex] = useState<number | null>(null);
  /*
   * Which steps this run will show. Held as state rather than derived, because
   * it must be answered at the moment the tour starts and then stay put: the
   * DOM moves underneath a running tour, and a total that changed mid-tour
   * would be worse than one that is merely wrong.
   */
  const [shownKeys, setShownKeys] = useState<readonly string[]>(() =>
    steps.map((s) => s.key),
  );
  /** Which way we are walking, so a skipped step skips the same way. */
  const directionRef = useRef<1 | -1>(1);
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const queryClient = useQueryClient();

  const running = index !== null;
  const step = running ? (steps[index] ?? null) : null;
  const anchor = useTutorialAnchor(step);

  /*
   * Every write is fire-and-forget against the cache, not awaited. A tour that
   * pauses on each step while a POST completes is worse than a tour whose
   * server state is a second behind, and there is nothing a failed write breaks
   * — the worst case is being offered the tour once more.
   */
  const eventMutation = useMutation({
    mutationFn: (input: { event: TutorialEvent; stepKey?: string }) =>
      recordTutorialEvent(input),
    onSuccess: (state) => {
      queryClient.setQueryData(TUTORIAL_STATE_KEY, state);
    },
  });
  const record = useCallback(
    (event: TutorialEvent, stepKey?: string): void => {
      eventMutation.mutate(stepKey === undefined ? { event } : { event, stepKey });
    },
    [eventMutation],
  );

  const start = useCallback(
    (fromStepKey?: string | null): void => {
      directionRef.current = 1;
      setShownKeys(showableSteps(steps).map((s) => s.key));
      setIndex(startIndexFor(fromStepKey, steps));
      record('started');
    },
    [record, steps],
  );

  const stop = useCallback((): void => {
    setIndex(null);
  }, []);

  const advance = useCallback(
    (delta: 1 | -1): void => {
      directionRef.current = delta;
      setIndex((current) => {
        if (current === null) return null;
        const nextIndex = current + delta;
        if (nextIndex < 0) return 0;
        if (nextIndex >= steps.length) return current;
        return nextIndex;
      });
    },
    [steps.length],
  );

  const next = useCallback((): void => {
    if (index === null) return;
    if (index >= steps.length - 1) {
      record('completed');
      stop();
      return;
    }
    advance(1);
  }, [advance, index, record, steps.length, stop]);

  const back = useCallback((): void => {
    advance(-1);
  }, [advance]);

  const skip = useCallback((): void => {
    record('declined');
    stop();
  }, [record, stop]);

  /*
   * Navigate to the step's route. Compared against pathname + search because
   * most of this platform's screens are `?tab=` views of a shared path — a
   * comparison on pathname alone would think it had already arrived.
   */
  useEffect(() => {
    if (!step?.route) return;
    const here = `${pathname}${search}`;
    if (here !== step.route) {
      navigate(step.route);
    }
  }, [navigate, pathname, search, step]);

  /* Record where they got to, so a resume can land here. */
  useEffect(() => {
    if (!step) return;
    record('step', step.key);
    // `record` is stable per mutation instance; re-running this on its identity
    // would write the same step twice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step?.key]);

  /*
   * The skip. A step that named an anchor and has not got one is not shown at
   * all — and the direction matters: skipping forwards on the way back would
   * bounce the user between two steps for ever.
   */
  useEffect(() => {
    if (!running || !step || anchor.state !== 'absent') return;
    const delta = directionRef.current;
    if (delta === 1) {
      if (index !== null && index >= steps.length - 1) {
        /*
         * The last step is unreachable — every remaining step was absent. End
         * the tour as COMPLETED rather than leaving the overlay up with nothing
         * in it. It genuinely is finished: there was nothing further to show
         * this account.
         */
        record('completed');
        stop();
        return;
      }
      advance(1);
      return;
    }
    if (index === 0) {
      /*
       * Walking backwards off the front. Stay put rather than close: the user
       * asked to go back, and closing the tour is not what Back means.
       * `welcome` names no anchor so this is only reachable if the first step
       * is ever given one.
       */
      directionRef.current = 1;
      advance(1);
      return;
    }
    advance(-1);
  }, [advance, anchor.state, index, record, running, step, steps.length, stop]);

  /*
   * Counted over the shown steps, so a skipped one leaves no gap. A step not in
   * the set contributes nothing, which means the number holds still for the one
   * frame before the resolver skips past it rather than flicking forward.
   */
  const shown = new Set(shownKeys);
  const position =
    index === null ? 0 : steps.slice(0, index + 1).filter((s) => shown.has(s.key)).length;

  return {
    step,
    position,
    total: shown.size,
    anchor,
    isFirst: index === 0,
    isLast: index !== null && index === steps.length - 1,
    running,
    start,
    next,
    back,
    skip,
  };
}
